import React, { useEffect, useState } from 'react';
import {
    T,
    E,
    usePortfolio,
    useUser,
    useSnaptrade
} from '../../logic/imports';
import * as Constants from '../../shared/Constants';
import { Modal } from '../app/Modal';
import { showDimmer } from '../app/Dimmer';
import { NumericInputModal } from '../app/CommonComps';

const supportedBrokerages = [
    E.Brokerage.PAPER,
    E.Brokerage.PUBLIC,
    E.Brokerage.WEBULL,
    E.Brokerage.WEBULL_CANADA
]

export const SettingsPanel: React.FC = () => {
    const [showChangelog, setShowChangelog] = useState(false);
    const [activatingAccountId, setActivatingAccountId] = useState<string | null>(null);
    const [paperAction, setPaperAction] = useState<'create' | 'reset' | null>(null);
    const [paperActionAccountId, setPaperActionAccountId] = useState<string | null>(null);
    const [isSubmittingPaperAction, setIsSubmittingPaperAction] = useState(false);
    /**
     * Used separately from useSnaptrade's isConnecting in order to also
     * encompass our own backend connection flow, which includes more than just
     * the connection portal being opened.
     */
    const [isConnectingBrokerage, setIsConnectingBrokerage] = useState(false);

    const { userData } = useUser();
    const { balances } = usePortfolio();
    const {
        accounts,
        activeAccountId,
        isConnecting,
        isLoading,
        isActivating,
        isResettingPaper,
        startConnection,
        activateAccount,
        activatePaperAccount,
        resetPaperAccount
    } = useSnaptrade();

    const hasPaperAccount = accounts.some((account) =>
        account.brokerage === E.Brokerage.PAPER || account.brokerAccountId.startsWith('paper-')
    );

    const nonPaperAccounts = accounts.filter((account) =>
        !(account.brokerage === E.Brokerage.PAPER || account.brokerAccountId.startsWith('paper-'))
    );
    const paperAccounts = accounts.filter((account) =>
        account.brokerage === E.Brokerage.PAPER || account.brokerAccountId.startsWith('paper-')
    );

    const accountsToDisplay: T.BrokerageCredentials[] = hasPaperAccount
        ? [...nonPaperAccounts, ...paperAccounts]
        : [
            ...nonPaperAccounts,
            {
                userId: userData.userId,
                brokerage: E.Brokerage.PAPER,
                brokerAccountId: 'paper-placeholder',
                accountName: 'Paper Trading Account',
                apiKey: 'paper-placeholder'
            }
        ];

    const handleConnectBrokerage = async () => {
        try {
            setIsConnectingBrokerage(true);
            await startConnection();
        } catch (error: any) {
            showDimmer(error || 'Failed to open connection portal', true);
        }
    };

    useEffect(() => {
        if (!isConnecting) {
            setIsConnectingBrokerage(false);
        }
    }, [isConnecting]);

    const handleActivateAccount = async (accountId: string) => {
        try {
            setActivatingAccountId(accountId);
            await activateAccount(accountId);
        } catch (error: any) {
            showDimmer(error.message || 'Failed to activate account', true);
        } finally {
            setActivatingAccountId(null);
        }
    };

    const handleActivatePaperAccount = async (accountId: string) => {
        try {
            setActivatingAccountId(accountId);
            await activatePaperAccount(0);
        } catch (error: any) {
            showDimmer(error?.message || 'Failed to activate paper account', true);
        } finally {
            setActivatingAccountId(null);
        }
    };

    const openPaperModal = (action: 'create' | 'reset', accountId: string | null = null) => {
        setPaperAction(action);
        setPaperActionAccountId(accountId);
    };

    const handleResetPaper = (accountId: string) => {
        openPaperModal('reset', accountId);
    };

    const handleSubmitPaperAction = async (startingCash: number) => {
        if (!paperAction) return;

        const submittedAction = paperAction;

        setPaperAction(null);
        setPaperActionAccountId(null);

        setIsSubmittingPaperAction(true);
        try {
            if (submittedAction === 'create') {
                await activatePaperAccount(startingCash);
            } else {
                await resetPaperAccount(startingCash);
            }

            showDimmer(
                submittedAction === 'create'
                    ? 'Paper trading account activated'
                    : 'Paper trading account reset successfully',
                true
            );
        } catch (error: any) {
            showDimmer(error?.message || 'Failed to process paper account action', true);
        } finally {
            setIsSubmittingPaperAction(false);
        }
    };

    return (
        <div className="panel">
            <div className="panel-header">Settings</div>

            {/* Brokerage Connection */}
            <div style={{ marginBottom: '20px' }}>
                {!activeAccountId ? (
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                        {/* <button
                            className='button'
                            onClick={handleUsePaperTrading}
                            disabled={isActivating || isResettingPaper || isSubmittingPaperAction}
                        >
                            Use Paper Trading
                        </button> */}
                        <button 
                            className='button'
                            onClick={handleConnectBrokerage}
                            disabled={isConnectingBrokerage}
                        >
                            {isConnectingBrokerage ? 'Connecting' : 'Connect SnapTrade'}
                        </button>
                    </div>
                ) : (
                    <button 
                        className='button'
                        style={{ marginBottom: '10px' }}
                        onClick={handleConnectBrokerage}
                        disabled={isConnectingBrokerage}
                    >
                        {isConnectingBrokerage ? 'Connecting' : 'Connect Brokerage Account'}
                    </button>
                )}

                {isConnectingBrokerage && (
                    <p style={{ 
                        fontSize: '0.9rem', 
                        color: '#888', 
                        marginTop: '5px',
                        fontStyle: 'italic' 
                    }}>
                        <span>Complete the connection in the popup window</span>
                        <span className="animated-ellipsis"></span>
                        <style>{`
                            @keyframes ellipsis {
                                0% { content: ""; }
                                20% { content: "."; }
                                40% { content: ".."; }
                                60% { content: "..."; }
                                80% { content: "...."; }
                                100% { content: "....."; }
                            }
                            .animated-ellipsis::after {
                                content: "...";
                                animation: ellipsis 1.5s infinite;
                                display: inline-block;
                                width: 1.5em;
                                text-align: left;
                            }
                        `}</style>
                    </p>
                )}

                {/* Display Connected Accounts */}
                {isLoading && (
                    <div style={{ color: '#888', fontSize: '0.9rem', marginTop: '10px' }}>
                        Loading accounts...
                    </div>
                )}

                {!isLoading && accountsToDisplay.length > 0 && (
                    <div style={{ marginTop: '15px' }}>
                        <div style={{ 
                            fontSize: '0.95rem', 
                            color: '#aaa', 
                            marginBottom: '10px',
                            fontWeight: 'bold'
                        }}>
                            Connected Accounts
                        </div>
                        {accountsToDisplay.map((account) => {
                            const isActive = activeAccountId === account.brokerAccountId;
                            const isPaper = account.brokerage === E.Brokerage.PAPER || account.brokerAccountId.startsWith('paper-');
                            const isPaperPlaceholder = account.brokerAccountId === 'paper-placeholder';

                            const accountDetails = isPaperPlaceholder
                                ? 'No paper account created yet'
                                : isPaper 
                                    ? 'Paper Trading Account'
                                    : `${account.accountName || 'Account'} • ••••${account.brokerAccountId.slice(-4)}`;

                            return (
                                <div 
                                    key={account.brokerAccountId}
                                    style={{
                                        backgroundColor: isActive ? '#2a3a2a' : '#2a2a2a',
                                        padding: '12px',
                                        borderRadius: '6px',
                                        marginBottom: '8px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        border: isActive ? '1px solid #4CAF50' : 'none'
                                    }}
                                >
                                    <div>
                                        <div style={{ 
                                            color: '#fff', 
                                            fontWeight: 'bold',
                                            marginBottom: '4px',
                                            textAlign: 'left'
                                        }}>
                                            {account.brokerage}
                                        </div>
                                        <div style={{ color: '#888', fontSize: '0.85rem', textAlign: 'left', lineHeight: '1.5' }}>
                                            {accountDetails}
                                            <br />
                                            {account.brokerAccountId === activeAccountId && balances && 'Portfolio connected'}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        {isActive ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <div style={{
                                                    backgroundColor: '#4CAF50',
                                                    color: '#fff',
                                                    padding: '6px 12px',
                                                    borderRadius: '4px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold'
                                                }}>
                                                    Active
                                                </div>
                                                {isPaper && (
                                                    <button
                                                        className='button'
                                                        onClick={() => handleResetPaper(account.brokerAccountId)}
                                                        disabled={isResettingPaper || isSubmittingPaperAction || isActivating}
                                                        style={{
                                                            padding: '6px 12px',
                                                            fontSize: '0.75rem',
                                                            height: 'auto',
                                                            minWidth: '80px',
                                                        }}
                                                    >
                                                        {isResettingPaper ? 'Resetting...' : 'Reset'}
                                                    </button>
                                                )}
                                            </div>
                                        ) : isPaperPlaceholder ? (
                                            <button
                                                className='button'
                                                onClick={() => openPaperModal('create')}
                                                disabled={isActivating || isResettingPaper || isSubmittingPaperAction}
                                                style={{
                                                    padding: '6px 12px',
                                                    fontSize: '0.75rem',
                                                    height: 'auto',
                                                    minWidth: '80px',
                                                }}
                                            >
                                                {isSubmittingPaperAction && paperAction === 'create' ? 'Creating...' : 'Create'}
                                            </button>
                                        ) : isPaper ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <button
                                                    className='button'
                                                    onClick={() => handleActivatePaperAccount(account.brokerAccountId)}
                                                    disabled={activatingAccountId !== null || isResettingPaper || isSubmittingPaperAction}
                                                    style={{
                                                        padding: '6px 12px',
                                                        fontSize: '0.75rem',
                                                        height: 'auto',
                                                        minWidth: '80px',
                                                    }}
                                                >
                                                    {activatingAccountId === account.brokerAccountId ? 'Activating...' : 'Activate'}
                                                </button>
                                            </div>
                                        ) : supportedBrokerages.includes(account.brokerage as E.Brokerage) ? (
                                            <button
                                                className='button'
                                                onClick={() => handleActivateAccount(account.brokerAccountId)}
                                                disabled={activatingAccountId !== null}
                                                style={{
                                                    padding: '6px 12px',
                                                    fontSize: '0.75rem',
                                                    height: 'auto',
                                                    minWidth: '80px',
                                                }}
                                            >
                                                {activatingAccountId === account.brokerAccountId ? 'Activating...' : 'Activate'}
                                            </button>
                                        ) : (
                                            <div style={{
                                                backgroundColor: '#444',
                                                color: '#999',
                                                padding: '6px 12px',
                                                borderRadius: '4px',
                                                fontSize: '0.75rem',
                                                fontWeight: 'bold'
                                            }}>
                                                Not Supported
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <NumericInputModal
                header={paperAction === 'create' ? 'Create Paper Account' : 'Reset Paper Account'}
                body={'Enter starting cash amount for the paper trading account.'}
                minValue={0}
                minMaxMessage={'Starting cash must be 0 or greater.'}
                onSubmit={handleSubmitPaperAction}
                onCancel={() => {
                    setPaperAction(null);
                    setPaperActionAccountId(null);
                }}
                isOpen={paperAction !== null}
            />
                

            {/* Version Info */}
            <button className='button' style={{
                fontSize: '1rem',
                height: '30px',
                display: 'flex',
                marginLeft: 'auto',
                justifyContent: 'center'
            }}
                onClick={() => setShowChangelog(true)
            }>
                Version {Constants.VERSION}
            </button>
            

            {/* Changelog Modal */}
            <Modal
                isOpen={showChangelog}
                title="Changelog"
                content={Constants.CHANGELOG_CONTENT}
                onAccept={() => setShowChangelog(false)}
                onDecline={() => setShowChangelog(false)}
                acceptButtonText="Close"
                requiresScrollToBottom={false}
                showAgreementCheckbox={false}
            />
        </div>
    );
}